import * as React from 'react';
import trim from 'lodash/trim';
import {Input} from '@packages/systems/design-system/Input';
import {Grid} from '@packages/systems/design-system/Grid';
import {Note, View, tokens} from '@packages/systems/spring';
import {Text} from '@packages/systems/design-system/Text';
import {styled} from '@packages/utilities/styled';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

import {Plugins, TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {matchJson} from '@packages/utilities/json';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {truncateMid} from '@packages/utilities/string-utils';
import {setIframeTitle} from '@packages/systems/core/utils/FigureUtils';
import {buildVideoEmbedValue} from './videoEmbedValue';

const {useReducer, useEffect} = React;
import {request} from '@packages/systems/core/utils/EmbedlyUtils';
import {type YouTubeParams} from '@packages/systems/core/utils/MediaUtils';

const InputLoadingIndicator = styled('div')(({loading, theme}) => ({
  display: loading ? 'block' : 'none',
  width: 6,
  height: 6,
  margin: 'auto 4px auto 8px',
  borderRadius: 100,
  background: theme.colors.backgroundDeepest,
}));

const STATUS = {
  DEFAULT: 'DEFAULT' as const,
  INVALID: 'INVALID' as const,
  LOADING: 'LOADING' as const,
} as const;

type StatusType = keyof typeof STATUS;

type LocalState = {
  status: StatusType;
  query: string;
  title: string;
  videoData?: any;
};

const defaultState: LocalState = {
  status: STATUS.DEFAULT,
  query: '',
  title: '',
  videoData: undefined,
};

const actionTypes = {
  URL_QUERY_CHANGED: 'URL_QUERY_CHANGED' as const,
  TITLE_CHANGED: 'TITLE_CHANGED' as const,
  VIDEO_FETCH_RESPONDED: 'VIDEO_FETCH_RESPONDED' as const,
  VIDEO_FETCH_REQUESTED: 'VIDEO_FETCH_REQUESTED' as const,
  VIDEO_CLEARED: 'VIDEO_CLEARED' as const,
} as const;

type LocalAction =
  | {
      type: 'URL_QUERY_CHANGED';
      payload: string;
    }
  | {
      type: 'TITLE_CHANGED';
      payload: string;
    }
  | {
      type: 'VIDEO_FETCH_RESPONDED';
      payload: {
        err: any;
        response:
          | {
              embed: {
                meta?: {
                  aspectRatio: number;
                  description: string;
                  height: number;
                  html: string;
                  source: string;
                  thumb: {
                    h: number;
                    url: string;
                    w: number;
                  };
                  title: string;
                  url: string;
                  width: number;
                  videoId?: string;
                  youTubeParams?: YouTubeParams;
                };
                url?: string;
                title?: string;
                type?: 'video';
              };
              style?: {
                ['padding-top']: string;
              };
              value?: string;
            }
          | null
          | undefined;
      };
    }
  | {
      type: 'VIDEO_FETCH_REQUESTED';
    }
  | {
      type: 'VIDEO_CLEARED';
    };

const localStateReducer = (
  state: LocalState,
  action: LocalAction
): LocalState => {
  switch (action.type) {
    case actionTypes.URL_QUERY_CHANGED: {
      return {
        ...state,
        query: action.payload,
        status: STATUS.DEFAULT,
        title: '',
      };
    }

    case actionTypes.VIDEO_FETCH_REQUESTED: {
      return {...state, status: STATUS.LOADING};
    }

    case actionTypes.VIDEO_FETCH_RESPONDED: {
      const {err, response} = action.payload;

      if (err) {
        return {...state, status: STATUS.INVALID};
      }

      return {
        ...state,
        videoData: response,
        status: STATUS.DEFAULT,
        title: response?.embed?.title ?? '',
      };
    }

    case actionTypes.TITLE_CHANGED: {
      return {...state, title: action.payload};
    }

    case actionTypes.VIDEO_CLEARED: {
      return {...state, videoData: {url: ''}, title: ''};
    }

    default:
      return state;
  }
};

const fetchEmbedly = async (
  // @ts-expect-error - TS7006 - Parameter 'localDispatch' implicitly has an 'any' type.
  localDispatch,
  nextUrl: string,
  // @ts-expect-error - TS7006 - Parameter 'currentUrl' implicitly has an 'any' type.
  currentUrl,
  dispatch: TypeImplementation.ExpressionEditorDispatch
) => {
  const query = trim(nextUrl);

  // If the input is empty, clear the video data
  if (query === '') {
    localDispatch({type: actionTypes.VIDEO_CLEARED});
    return;
  }

  // If the video node data embed url is the same, do nothing
  if (currentUrl === query) {
    return;
  }

  localDispatch({type: actionTypes.VIDEO_FETCH_REQUESTED});

  let payload;
  try {
    let nextVideoData;
    const response = await request(query);

    // If videoData was changed successfully, dispatch the new payload to the
    // video node dataType settings to update
    if (response) {
      nextVideoData = buildVideoEmbedValue(response);
      dispatch(updateExpressionAction(createLiteralExpr(nextVideoData)));
    }

    payload = {
      err: false,
      response: nextVideoData,
    };
  } catch (err: any) {
    payload = {
      err,
      response: null,
    };
  }

  localDispatch({
    type: actionTypes.VIDEO_FETCH_RESPONDED,
    payload,
  });
};

const defaultVideoData = {
  embed: {title: '', url: '', meta: {}},
} as const;
const getEmbedVideoData = (expr: ContextualExpression) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  return literal
    ? matchJson({
        Null: () => defaultVideoData,
        Boolean: () => defaultVideoData,
        Number: () => defaultVideoData,
        String: () => defaultVideoData,
        Array: () => defaultVideoData,
        Object: (fields) => Object.assign(defaultVideoData, fields),
      })(literal.value)
    : defaultVideoData;
};

// We assume the `value` is in correct shape here and thus set it to any
const createLiteralExpr = (value: any) =>
  ELiteral({
    name: qualify(Plugins.name('Embed'), typeName('Video')),
    value,
  });

export const VideoPluginType = TypeImplementation.Custom({
  renderSummary: function VideoPluginTypeRenderSummary({
    expr,
  }: {
    expr: ContextualExpression;
  }) {
    const embedVideoData = getEmbedVideoData(expr);
    const {
      embed: {title: embedTitle, url: embedUrl},
    } = embedVideoData;

    if (!embedUrl || embedUrl === '') {
      return <span>No video</span>;
    }

    if (embedTitle && embedTitle !== '') {
      const truncatedTitle = truncateMid(embedTitle, 30);
      return <span>{truncatedTitle}</span>;
    }

    return <span>Video</span>;
  },
  render: function VideoPluginSettings({expr, dispatch}) {
    const {env} = useExpressionEditorImplementationContext();
    const embedVideoData = getEmbedVideoData(expr);
    const {
      embed: {title: embedTitle, url: embedUrl},
    } = embedVideoData;

    const [localState, localDispatch] = useReducer(localStateReducer, {
      ...defaultState,
      query: embedUrl,
      title: embedTitle,
      videoData: embedVideoData,
    });
    const {status, videoData, query, title} = localState;

    useEffect(
      () => {
        if (embedUrl != null && embedUrl !== localState.query) {
          localDispatch({
            type: actionTypes.URL_QUERY_CHANGED,
            payload: embedUrl,
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [embedUrl]
    );

    useEffect(
      () => {
        if (embedTitle !== localState.title) {
          localDispatch({
            type: actionTypes.TITLE_CHANGED,
            payload: embedTitle,
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [embedTitle]
    );

    const onUrlChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      localDispatch({
        type: actionTypes.URL_QUERY_CHANGED,
        payload: event.target.value,
      });

    const onUrlBlur = () => {
      fetchEmbedly(localDispatch, query, embedUrl, dispatch);
    };

    const onUrlKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        fetchEmbedly(localDispatch, query, embedUrl, dispatch);
      }
    };

    const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      localDispatch({
        type: actionTypes.TITLE_CHANGED,
        payload: event.target.value,
      });

    const setTitle = () => {
      const nextVideoData = {
        ...videoData,
        embed: {
          ...videoData?.embed,
          title,
        },
        value: setIframeTitle(videoData?.embed?.meta?.html || '', title),
      } as const;
      dispatch(updateExpressionAction(createLiteralExpr(nextVideoData)));
    };

    const onTitleBlur = () => {
      setTitle();
    };

    const onTitleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setTitle();
      }
    };

    const loading = status === STATUS.LOADING;

    const isOverride = env.context === 'override';

    const viewStyles = isOverride
      ? {
          border: '$border1',
          padding: '$2',
          borderRadius: '$2',
        }
      : null;

    const textColor = isOverride ? tokens.colors.textSecondary : 'inherit';

    return (
      <View {...viewStyles}>
        <Grid
          gridTemplateColumns="44px 1fr"
          gridColumn="1/-1"
          gridColumnGap={2}
          gridRowGap={isOverride ? 2 : 1}
        >
          <Text
            style={{margin: 'auto 0', color: textColor}}
            data-automation-id="symbols-dataType-contentType"
          >
            URL
          </Text>
          <Input
            data-automation-id="video-plugin-type-input"
            gridColumn="2"
            value={query}
            onChange={onUrlChange}
            onKeyDown={onUrlKeyDown}
            onBlur={onUrlBlur}
            invalid={status === STATUS.INVALID}
            placeholder="e.g. https://youtu.be/Ojiv9Smi4XE"
            suffix={
              <InputLoadingIndicator
                data-automation-id="video-field-validation-status-indicator"
                data-status={status}
                className={loading ? 'animate-blink' : ''}
                loading={loading}
              />
            }
          />

          {status === STATUS.INVALID && (
            <Note style={{gridColumn: 2}} colorVariant="danger" hasIcon={false}>
              {'Invalid video link'}
            </Note>
          )}

          <Text
            style={{margin: 'auto 0', color: textColor}}
            data-automation-id="symbols-dataType-contentType"
          >
            Title
          </Text>
          <Input
            data-automation-id="video-plugin-title-input"
            gridColumn="2"
            value={title}
            onKeyDown={onTitleKeyDown}
            onChange={onTitleChange}
            onBlur={onTitleBlur}
          />
        </Grid>
      </View>
    );
  },
  system: {
    isFullWidth: true,
    bindUIVariant: 'label',
    defaultValue: {
      embed: {
        type: 'video',
        url: '',
        title: '',
      },
    },
  },
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Embed'), iconName('Video'))
  ),
});
